<template>
  <div class="flex h-full min-h-0 flex-col">
    <div class="flex shrink-0 flex-col gap-2 p-2">
      <app-brand-mark />

      <company-switcher v-if="hasManagedCompanies" @navigate="() => $emit('navigate')" />
    </div>

    <div class="flex min-h-0 flex-1 flex-col justify-between overflow-y-auto">
      <div>
        <ui-sidebar-section
          v-for="(section, index) of visibleSections"
          :key="'sidebar-section-' + index"
        >
          <router-link
            v-for="item of section"
            v-slot="{ href, navigate }"
            :key="item.to"
            :to="item.to"
            custom
          >
            <ui-sidebar-nav-item
              :href="href"
              :active="isActiveItem(item)"
              @click="(e) => onNavigate(e, navigate)"
            >
              <component :is="item.icon" />
              {{ tr(item.label) }}
              <ui-sidebar-nav-badge v-if="item.badge">
                {{ item.badge }}
              </ui-sidebar-nav-badge>
            </ui-sidebar-nav-item>
          </router-link>
        </ui-sidebar-section>

        <ui-sidebar-section>
          <ui-sidebar-nav-item :href="userGuideLink" target="_blank">
            <book-open-icon />
            {{ tr('User guide') }}
          </ui-sidebar-nav-item>
        </ui-sidebar-section>
      </div>

      <div class="flex flex-col gap-1 p-2">
        <language-select />

        <div class="flex items-center justify-between gap-2">
          <ui-theme-toggle
            v-model="theme"
            :light-label="tr('Switch to light mode')"
            :dark-label="tr('Switch to dark mode')"
          />
          <ui-button
            variant="ghost"
            size="icon"
            class="text-muted-foreground"
            :aria-label="tr('Log out')"
            :title="tr('Log out')"
            @click="() => logout()"
          >
            <log-out-icon />
          </ui-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  BookOpen,
  Bug,
  ChartColumn,
  Clock,
  Inbox,
  LayoutDashboard,
  LogOut,
  Newspaper,
  Plug,
  Rocket,
  Settings,
  Sparkles,
  Store,
  UserRound,
} from '@lucide/vue';

import AppBrandMark from '@/ui/components/AppBrandMark.vue';
import CompanySwitcher from '@/ui/components/CompanySwitcher.vue';
import LanguageSelect from '@/ui/components/LanguageSelect.vue';
import { canAccessPath } from '@/utils/routeAccess.js';

export default {
  name: 'AppSidebar',
  components: {
    AppBrandMark,
    BookOpenIcon: BookOpen,
    CompanySwitcher,
    LanguageSelect,
    LogOutIcon: LogOut,
  },
  emits: [ 'navigate' ],
  computed: {
    sections() {
      return [
        [
          { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
          { to: '/brands', label: 'Brands', icon: Store },
          { to: '/automation', label: 'Automations', icon: Rocket },
          { to: '/waiting-list', label: 'Waiting List', icon: Clock },
          { to: '/posts', label: 'Posts', icon: Newspaper },
          { to: '/creator-lite', label: 'Infinite∞Creator', icon: Sparkles, badge: 'Lite' },
          { to: '/report', label: 'Report', icon: ChartColumn },
          { to: '/leadgen-forms', label: 'Leads', icon: Inbox },
        ],
        [
          { to: '/integrations', label: 'Integrations', icon: Plug },
          { to: '/error-logs', label: 'Error log', icon: Bug },
          { to: '/settings', label: 'Settings', icon: Settings },
          { to: '/account', label: 'Account', icon: UserRound, hidden: this.$app.isShoprenterApp },
        ],
      ];
    },
    visibleSections() {
      return this.sections.map(section => section.filter(item => !item.hidden && canAccessPath(this.$router, item.to)));
    },
    hasManagedCompanies() {
      if (this.$root.isUserFetching || !this.$root.loggedInUser) {
        return false;
      }

      const managedCompanies = this.$root.loggedInUser.company.managed_companies;
      return Array.isArray(managedCompanies) && managedCompanies.length > 0;
    },
    userGuideLink() {
      const baseUrl = 'https://docs.infinite.tools';
      const languagePath = this.$root.language !== 'en' ? '/' + this.$root.language : '';

      if (this.$app.isShoprenterApp) {
        return baseUrl + '/shoprenter' + languagePath;
      }

      return baseUrl + languagePath + '/ad';
    },
    theme: {
      get() {
        return this.$store.getters['app/currentTheme'];
      },
      set(value) {
        this.$store.commit('app/updateTheme', value);
      },
    },
  },
  methods: {
    // By path prefix: router-link's isActive only follows nested route
    // records, and e.g. /automation/:id/overview is not nested in /automation.
    isActiveItem(item) {
      return this.$route.path === item.to || this.$route.path.startsWith(item.to + '/');
    },
    onNavigate(e, navigate) {
      navigate(e);
      this.$emit('navigate');
    },
    logout() {
      this.$store.dispatch('auth/logout');
    },
  },
};
</script>
